<template>
  <wizard
    first-step="personalDetails"
    class="wizard-throughput-kdv-bso"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="selectChildren"
      :model="formData"
      :next-step="submitSelectedChildren"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.selectChildren.title }}</h2>
        <slot name="select-children-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <h3>{{ i18n.form.labels.childRegistrationItems }}</h3>

        <FormulateInput
          type="group"
          name="children"
          :help="i18n.form.help.childRegistrationForm"
          :add-label="i18n.form.addlabel.childRegistrationForm"
          :repeatable="true"
          validation="bail|min:1,length|max:5,length|unique:childRegistrationForm"
          :validation-messages="i18n.form.validations.childRegistrationItems"
          @repeatableRemoved="resetChangesPerChild"
        >
          <div class="row">
            <!-- workaround for losing focus on first input -->
            <FormulateInput
              type="hidden"
              value="none"
            />
            <!-- /workaround -->
            <FormulateInput
              type="text"
              name="name"
              :label="i18n.form.labels.childName"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.childName"
            />
          </div>
        </FormulateInput>
      </fieldset>
    </wizard-step>

    <wizard-step
      v-for="(indexPlusOne, index) in 5"
      :key="'locationForChild-' + index"
      :i18n="i18n"
      :name="'locationForChild-' + index"
      :model="formData"
      :next-step="
        indexPlusOne < formData.selectChildren.children.length
          ? 'locationForChild-' + indexPlusOne
          : 'throughputForChild-0'
      "
      :previous-step="index > 0 ? 'locationForChild-' + (index - 1) : 'selectChildren'"
    >
      <wizard-step-intro>
        <h2>
          {{
            i18n.wizardSteps.locationForChild.title.replace(
              '{name child}',
              formData.selectChildren.children[index]
                ? formData.selectChildren.children[index].name
                : '',
            )
          }}
        </h2>
        <slot name="location-for-child-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <fields-date-picker
          field-name="date"
          :i18n="i18n"
          :min-date="minDateForChange"
          :date-validation="'bail|required|after:' + dateBeforeMinDateForChange"
        />

        <fields-dropdown-cities
          :i18n="i18n"
          :label="label"
          field-name="city"
        />
        <fields-dropdown-city-locations
          :i18n="i18n"
          :label="label"
          field-name="location"
          :is-required="true"
          :city="
            formData['locationForChild-' + index]
              ? formData['locationForChild-' + index].city
              : null
          "
          careTypeFilter="BSO"
        />

        <FormulateInput
          :label="i18n.form.labels.schoolname"
          name="schoolName"
          validation="nohtml"
          :validation-messages="i18n.form.validations.schoolname"
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      v-for="(indexPlusOne, index) in 5"
      :key="'throughputForChild-' + index"
      :i18n="i18n"
      :name="'throughputForChild-' + index"
      :model="formData"
      :next-step="
        indexPlusOne < formData.selectChildren.children.length
          ? 'throughputForChild-' + indexPlusOne
          : 'review'
      "
      :previous-step="
        index > 0
          ? 'throughputForChild-' + (index - 1)
          : 'locationForChild-' + (formData.selectChildren.children.length - 1)
      "
    >
      <wizard-step-intro>
        <h2>
          {{
            i18n.wizardSteps.throughputForChild.title.replace(
              '{name child}',
              formData.selectChildren.children[index]
                ? formData.selectChildren.children[index].name
                : '',
            )
          }}
        </h2>
        <slot name="throughput-for-child-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="productTypes"
          :options="{
            preschoolcare: i18n.form.labels.producttypeoptions.preschoolcare,
            afterschoolcare: i18n.form.labels.producttypeoptions.afterschoolcare,
          }"
          type="checkbox"
          :label="i18n.form.labels.producttype"
          validation="required"
          :validation-messages="i18n.form.validations.producttype"
        />
      </fieldset>

      <fields-product-days
        v-if="isProductTypeSelectedForChildWithIndex(index, 'preschoolcare')"
        :i18n="i18n"
        :i18n-description="i18n.form.labels.daypartsvso"
        :i18n-cancellation="i18n.form.labels.cancelvso"
        name-prefix="preschoolCare"
      />

      <fields-product-days
        v-if="isProductTypeSelectedForChildWithIndex(index, 'afterschoolcare')"
        :i18n="i18n"
        :i18n-description="i18n.form.labels.daysbso"
        :i18n-cancellation="i18n.form.labels.cancelbso"
        name-prefix="afterSchoolCare"
      />

      <FormulateInput
        name="vacation"
        :label="i18n.form.labels.vacation"
        type="checkbox"
      />

      <FormulateInput
        name="remarkYesNo"
        :options="{
          yes: i18n.form.labels.remarkYesNoOptions.yes,
          no: i18n.form.labels.remarkYesNoOptions.no,
        }"
        type="radio"
        :label="i18n.form.labels.remarkYesNo"
        validation="required"
        :validation-messages="i18n.form.validations.remarkYesNo"
      />

      <FormulateInput
        v-if="areThereRemarksForChildWithIndex(index)"
        name="remark"
        type="textarea"
        validation="nohtml"
        :validation-messages="i18n.form.validations.remark"
      />
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      :previous-step="'throughputForChild-' + (formData.selectChildren.children.length - 1)"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <th>{{ i18n.form.labels.reviewName }}</th>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewEmail }}</th>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewPhoneNumber }}</th>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewCustomerNumber }}</th>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>
          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>

          <template v-for="(child, childIndex) of resultingData.changesPerChild">
            <tr :key="child.childName + '-name'">
              <td colspan="2">
                <h3>{{ child.childName }}</h3>
              </td>
            </tr>
            <tr
              v-if="child.throughputKdvBso.preschoolCareDays"
              :key="child.childName + '-preschoolCare'"
            >
              <th>{{ i18n.form.labels.producttypeoptions.preschoolcare }}</th>
              <td>
                {{
                  child.throughputKdvBso.preschoolCareDays
                    .map((d) => translateDayPart(d))
                    .join(', ')
                }}
              </td>
            </tr>
            <tr
              v-if="child.throughputKdvBso.afterSchoolCareDays"
              :key="child.childName + '-afterSchoolCare'"
            >
              <th>{{ i18n.form.labels.producttypeoptions.afterschoolcare }}</th>
              <td>
                {{
                  child.throughputKdvBso.afterSchoolCareDays
                    .map((d) => i18n.form.labels.daysoptions[d])
                    .join(', ')
                }}
              </td>
            </tr>
            <tr :key="child.childName + '-careDuringHoliday'">
              <th>{{ i18n.form.labels.reviewCareDuringHoliday }}</th>
              <td>
                {{
                  child.throughputKdvBso.noCareDuringHoliday
                    ? i18n.form.labels.reviewCareDuringHolidayOptions.yes
                    : i18n.form.labels.reviewCareDuringHolidayOptions.no
                }}
              </td>
            </tr>
            <tr :key="child.childName + '-date'">
              <th>{{ i18n.form.labels.reviewChangeDate }}</th>
              <td>{{ moment(child.locationChange.date).format('DD-MM-YYYY') }}</td>
            </tr>
            <tr
              v-if="child.locationChange.city"
              :key="child.childName + '-city'"
            >
              <th>{{ i18n.form.labels.reviewDesiredCity }}</th>
              <td>{{ child.locationChange.city }}</td>
            </tr>
            <tr
              v-if="child.locationChange.location"
              :key="child.childName + '-location'"
            >
              <th>{{ i18n.form.labels.reviewDesiredLocation }}</th>
              <td>{{ child.locationChange.location }}</td>
            </tr>
            <tr
              v-if="child.locationChange.schoolName"
              :key="child.childName + '-schoolName'"
            >
              <th>{{ i18n.form.labels.reviewSchoolName }}</th>
              <td>{{ child.locationChange.schoolName }}</td>
            </tr>
            <tr :key="child.childName + '-remark'">
              <th>{{ i18n.form.labels.reviewRemark }}</th>
              <td>
                {{ child.throughputKdvBso.remark ? child.throughputKdvBso.remark : '' }}
              </td>
            </tr>
            <tr
              v-if="childIndex < resultingData.changesPerChild.length - 1"
              :key="child.childName + '-mb'"
            >
              <td colspan="2">
                {{ '\xa0' }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
  import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
  import FieldsDropdownCities from '@/components/forms/fields/FieldsDropdownCities.vue';
  import FieldsDropdownCityLocations from '@/components/forms/fields/FieldsDropdownCityLocations.vue';
  import FieldsProductDays from '@/components/forms/fields/FieldsProductDays.vue';
  import Wizard from '@/components/wizards/components/Wizard.vue';
  import WizardStep from '@/components/wizards/components/WizardStep.vue';
  import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
  import utils from '@/lib/utils';
  import axios from 'axios';
  import moment from 'moment';
  import getQueryParams from '@/lib/formulate/getFormQueryParam';

  export default {
    name: 'WizardThroughputKdvBso',
    components: {
      FieldsDatePicker,
      FieldsDropdownCityLocations,
      FieldsDropdownCities,
      FieldsProductDays,
      Wizard,
      WizardStep,
      WizardStepIntro,
    },
    props: {
      i18n: {
        type: Object,
        default: null,
      },
      prefillData: {
        type: Object,
        default: () => { },
      },
      locale: {
        type: String,
        default: () => 'nl',
      },
      label: {
        type: String,
        default: 'Partou',
      },
      minDateForChange: {
        type: String,
        default: () => null,
      },
      successPageUrl: {
        type: String,
        default: () => '/',
      },
      formToken: {
        type: String,
        default: () => '',
      },
    },
    data() {
      return {
        formData: {
          personalDetails: this.$store.state.wizard.personalDetails,
          selectChildren: this.$store.state.wizard.selectedChildren,
        },
        request: {
          isSending: false,
          errorSending: false,
        },
      };
    },
    computed: {
      moment() {
        return moment;
      },
      dateBeforeMinDateForChange() {
        return utils.date.getDate(new Date(this.minDateForChange), -1);
      },
      resultingData() {
        return {
          label: this.label,
          locale: this.locale,
          formToken: this.formToken,
          personalDetails: this.formData.personalDetails,
          changesPerChild: this.formData.selectChildren.children
            .map((child, childIndex) => {
              const locationData = this.formData[`locationForChild-${childIndex}`];
              const throughputData = this.formData[`throughputForChild-${childIndex}`];
              return {
                childName: child.name,
                throughputKdvBso: {
                  productTypes: throughputData?.productTypes,
                  preschoolCareDays: throughputData?.preschoolCareFutureDays
                    ? throughputData.preschoolCareFutureDays.map((d) => `${d}morning`)
                    : undefined,
                  afterSchoolCareDays: throughputData?.afterSchoolCareFutureDays,
                  noCareDuringHoliday: throughputData?.vacation,
                  remark: throughputData?.remark,
                },
                locationChange: {
                  date: locationData?.date,
                  city: locationData?.city,
                  location: locationData?.location,
                  schoolName: locationData?.schoolName,
                },
              };
            }),
        };
      },
    },
    created() {
      this.formData.personalDetails = {
        ...this.formData.personalDetails,
        ...getQueryParams(this.prefillData)
      }
    },
    methods: {
      isProductTypeSelectedForChildWithIndex(index, productType) {
        return this.formData[`throughputForChild-${index}`]?.productTypes?.includes(productType);
      },
      areThereRemarksForChildWithIndex(index) {
        return this.formData[`throughputForChild-${index}`]?.remarkYesNo === 'yes';
      },
      translateDayPart(dayPart) {
        return utils.translate.translateDayPart(dayPart, this.i18n);
      },
      submitPersonalDetails() {
        this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
        return 'selectChildren';
      },
      submitSelectedChildren() {
        this.$store.dispatch('wizard/setSelectedChildren', this.formData.selectChildren);
        return 'locationForChild-0';
      },
      resetChangesPerChild() {
        Object.keys(this.formData)
          .filter((k) => k.startsWith('locationForChild-'))
          .forEach((k) => (this.formData[k] = {}));
      },
      sendRequest() {
        this.request.errorSending = false;
        this.request.isSending = true;

        const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

        const config = {
          headers: {
            'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
          }
        };

        axios
          .post('/umbraco/api/wizard/throughputkdvbso', this.resultingData, config)
          .then(() => {
            window.location.href = this.successPageUrl;
          })
          .catch(() => {
            this.request.isSending = false;
            this.request.errorSending = true;
          });
      },
    },
  };
</script>
